import { useReactiveVar } from '@apollo/client';
import AccountRemovalErrors from 'components/account/AccountRemovalErrors';
import AccountRemovalFailed from 'components/account/AccountRemovalFailed';
import AccountRemovalPrevented from 'components/account/AccountRemovalPrevented';
import AccountRemovalPreventedLinkedService from 'components/account/AccountRemovalPreventedLinkedService';
import AccountRemovalSuccess from 'components/account/AccountRemovalSuccess';
import AccountRestrictedModalContent from 'components/account/AccountRestrictedModalContent';
import AppLocation from 'components/account/AppLocation';
import ConfirmVehicleRemoval from 'components/account/ConfirmVehicleRemoval';
import Logout from 'components/account/Logout';
import RemoveAccount from 'components/account/RemoveAccount';
import UnsavedEditVehicleConfirm from 'components/account/UnsavedEditVehicleConfirm';
import BenefitDetailModal from 'components/account/benefits/BenefitDetailModal';
import DisconnectBenefitModal from 'components/account/benefits/DisconnectBenefitModal';
import EmailVerificationModal from 'components/account/benefits/EmailVerificationModal';
import SetParkingReminderModalContent from 'components/account/notifications/SetParkingReminderModalContent';
import ConfirmPaymentMethodRemoval from 'components/account/paymentMethods/ConfirmPaymentMethodRemoval';
import CorporateAccountContent from 'components/account/paymentMethods/CorporateAccountContent';
import PaymentCardContent from 'components/account/paymentMethods/PaymentCardContent';
import SwishInfo from 'components/account/paymentMethods/SwishInfo';
import UnsavedEditPermitConfirm from 'components/account/permit/UnsavedEditPermitConfirm';
import ConfirmRfidTagRemoval from 'components/account/rfid/ConfirmRfidTagRemoval';
import EditOrDeleteRfid from 'components/account/rfid/EditOrRemoveRfid';
import RegisterRfidModalContent from 'components/account/rfid/RegisterRfidModalContent';
import RfidScanCompleteModal from 'components/account/rfid/RfidScanCompleteModal';
import RfidTagScanModal from 'components/account/rfid/RfidStartScanModal';
import CancelSubscriptionModal from 'components/account/subscription/CancelSubscriptionModal';
import LanguageList from 'components/language/LanguageList';
import BillingIntervalsModalContent from 'components/longTermParking/BillingIntervalsModalContent';
import ZoneFilterModal from 'components/map/ZoneFilterModal';
import ExtendOngoingParkingConfirm from 'components/ongoingParking/ExtendParkingConfirm';
import NewParkingSessionPriceBreakdownDetails from 'components/ongoingParking/NewParkingSessionPriceBreakdownDetails';
import OngoingMultipleSessionsModalContent from 'components/ongoingParking/OngoingMultipleSessionsModalContent';
import OngoingParkingSessionPriceBreakdownDetails from 'components/ongoingParking/OngoingParkingSessionPriceBreakdownDetails';
import StopOngoingParkingConfirm from 'components/ongoingParking/StopParkingConfirm';
import StopParkingConfirmAnprManual from 'components/ongoingParking/StopParkingConfirmAnprManual';
import AnprPaymentMethodModalContent from 'components/park/AnprPaymentMethodModalContent';
import ConfirmParkingClosedZoneModalContent from 'components/park/ConfirmParkingClosedZoneModalContent';
import SelectPaymentMethodModalContent from 'components/park/SelectPaymentMethodModalContent';
import SelectVehicleModalContent from 'components/park/SelectVehicleModalContent';
import SelectVehiclesModalContent from 'components/park/SelectVehiclesModalContent';
import UnlockMemberGroupPmcModalContent from 'components/park/UnlockMemberGroupPmcModalContent.tsx';
import UnlockPmcModalContent from 'components/park/UnlockPmcModalContent';
import UpdateFeatureCountryModal from 'components/park/UpdateFeatureCountryModal';
import OpenBarrierManually from 'components/park/detail/OpenBarrierManually';
import CancelCharging from 'components/park/evcharge/CancelCharging';
import StopCharging from 'components/park/evcharge/StopCharging';
import DefaultPaymentMethodModalContent from 'components/park/pooling/DefaultPaymentMethodModalContent';
import NoPaymentRequiredModalContent from 'components/park/pooling/NoPaymentRequiredModalContent';
import UnlockTariffModalContent from 'components/park/tariff/UnlockTariffModalContent';
import AddOtherPaymentMethod from 'components/payment/AddOtherPaymentMethod';
import AdyenCardAddContainer from 'components/payment/AdyenCardAddContainer';
import CreditCardForm from 'components/payment/CreditCardForm';
import MobilePayConfirmation from 'components/payment/MobilePayConfirmation';
import WalletTokenEdit from 'components/payment/WalletTokenEdit';
import FirstTimer from 'components/tutorial/FirstTimer';
import RemoteContentTutorial from 'components/tutorial/RemoteTutorial';
import { useAdyenCardAddEnabled } from 'hooks/useAdyenCardAddEnabled';
import React from 'react';
import { useTranslation } from 'react-i18next';
import ContactUsSelectCountry from 'screens/account/ContactUsSelectCountry';
import ParkingHistoryFilterModalContent from 'screens/account/ParkingHistoryFilterModalContent';
import TermsPdfModalContent from 'screens/account/TermsPdfModalContent';
import HslTicketRenderModalContent from 'screens/hslTicket/HslTicketRenderModalContent';
import { UnavailableModalContent } from 'screens/longTermParking';
import LeaveWaitingListModal from 'screens/longTermParking/waitingList/LeaveWaitingListModal';
import ConfirmCancelPermitModalContent from 'screens/permit/ConfirmCancelPermitModalContent';
import ConfirmPurchasePermitModalContent from 'screens/permit/ConfirmPurchasePermitModalContent';
import ItemUnavailableModalContent from 'screens/permit/ItemUnavailableModalContent';
import LimitReachModalContent from 'screens/permit/LimitReachModalContent';
import PermitOrderLineModalContent from 'screens/permit/PermitOrderLineModalContent';
import PermitParkingSpaceModalContent from 'screens/permit/PermitParkingSpaceModalContent.tsx';
import PermitHsaModalContent from 'screens/permit/PermitHsaModalContent';
import PrepaidTicketConfirmModalContent from 'screens/prepaidTickets/PrepaidTicketConfirmModalContent';
import {
  confirmCreateSalesOrderData,
  unlockMemberGroupSuccess,
} from 'states/common';
import { useActiveElementStore } from 'stores/activeElementStore';
import { useAuthStore } from 'stores/authStore';
import { useHslStore } from 'stores/hslStore';
import { usePaymentStore } from 'stores/paymentStore';
import { resolveAddCardScreenTitle } from 'utils/paymentUtils';
import { usePermitStore } from 'stores/permitStore';
import { useRfidStore } from 'stores/rfidStore';
import { LicensePlate } from 'types/generatedSchemaTypes';
import { getModalButtonType } from 'utils/commonUtils';
import { titleTypeText } from 'utils/hslUtils';
import RNWebModal from '../common/RNWebModal';
import OpenHoursModalContent from '../park/OpenHoursModalContent';
import UnlockMemberGroupModalContent from 'components/park/UnlockMemberGroupModalContent';
import { useUiStore } from 'stores/uiStore';

const SubscriptionPaymentMethodModalContent = () => (
  <SelectPaymentMethodModalContent recurringOnly />
);

const Empty = () => <></>;
const CreditCardFormComponent = () => <CreditCardForm isModal={true} />;
const CorporateAccountContentComponent = () => <CorporateAccountContent />;

// Adyen card-add for the addPaymentMethod sheet. Mirrors the Nets CreditCardForm
// success/close behavior in this modal: the Nets path (no afterSuccess) lands on
// the card-added modal via setSheetModal('paymentCard'); a cancel/dismiss is
// setSheetModal(''). The container keeps the stashed session on unmount (every
// open re-inits it); an unmount while the card is still pre-submit sends the
// abandon cancel, which discards the never-activated card server-side.
const AdyenAddPaymentMethodComponent = () => (
  <AdyenCardAddContainer
    onSuccess={() => useUiStore.getState().setSheetModal('paymentCard')}
    onClose={() => useUiStore.getState().setSheetModal('')}
  />
);

const Modal: React.FC = () => {
  const { t } = useTranslation();
  const modalType = useUiStore((state) => state.sheetModal);
  const paymentCard = usePaymentStore((s) => s.selectedPaymentCard);
  const addCardWalletType = usePaymentStore(
    (s) => s.adyenCardAddSession?.walletType
  );
  const hslTicket = useHslStore((state) => state.selectedHslTicket);
  const activeZoneId = useActiveElementStore((state) => state.activeElement);
  const canContinueRemoval = useAuthStore(
    (state) => state.accountRemoveWithWarnings
  );
  const selectedPMC = usePermitStore((state) => state.selectedPmc);
  const confirmDisableRenewalOnly = usePermitStore(
    (state) => state.confirmDisableRenewalOnly
  );
  const showDisableRenewalDialog = usePermitStore(
    (state) => state.showDisableRenewalDialog
  );
  const { meta: { freeSalesOrder = false } = {} } =
    confirmCreateSalesOrderData() || {};
  const { hsaIdState } = usePermitStore();
  const unlockMemberGroupSuccessVar = useReactiveVar(unlockMemberGroupSuccess);

  const maxLicensePlates = selectedPMC?.maxLicensePlatesPerQuantity ?? 1;
  const tagName = useRfidStore((state) => state.currentTagName);
  // Adyen card-add provider gate (flag + app country + email; dark by default,
  // no Nets fallback once true). Shared across the entry hook, the add-payment
  // modal (native + web) and onboarding so all gate identically. When true the
  // addPaymentMethod sheet renders the Adyen flow; otherwise the existing Nets
  // CreditCardForm is untouched.
  const useAdyen = useAdyenCardAddEnabled();
  let Component: React.FC<any> = Empty;
  let headline;
  let subtitle;
  let allowScrollableContent = true;
  //Right now these 3 are not used in any specific modalContent, but the modal
  // has the capacity to use them, when needed again change the corresponding
  // variable to let
  const backDropOpacity = 0.25;
  const forceOpen = false;
  const hideCloseButton = false;

  switch (modalType) {
    case 'parkingHours':
      Component = OpenHoursModalContent;
      headline = t('parking.openHours');
      break;
    case 'signOut':
      Component = Logout;
      headline = t('account.signOutConfirmHeader');
      break;
    case 'addPaymentMethod':
      Component = useAdyen
        ? AdyenAddPaymentMethodComponent
        : CreditCardFormComponent;
      headline = resolveAddCardScreenTitle(
        t,
        paymentCard.status,
        addCardWalletType
      );
      allowScrollableContent = false;
      break;
    case 'corporateAccount':
      Component = CorporateAccountContentComponent;
      headline = t('paymentMethods.corporateAccount');
      break;
    case 'parkingVehicle':
      Component = SelectVehicleModalContent;
      headline = t('parking.parkingProcess.selectVehicle');
      break;
    case 'parkingVehicles':
      Component = () => (
        <SelectVehiclesModalContent
          maxLicensePlates={maxLicensePlates}
          handleOnChange={(licensePlates: LicensePlate[]) => {
            usePermitStore
              .getState()
              .setSelectedLicensePlatesPermitRedeem(licensePlates);
          }}
        />
      );
      break;
    case 'stopOngoingParking':
      Component = StopOngoingParkingConfirm;
      headline = t('parking.stopParkingConfirm');
      break;
    case 'stopOngoingParkingAnpr':
      Component = StopParkingConfirmAnprManual;
      headline = t('parking.stopParkingConfirm');
      break;
    case 'extendOngoingParking':
      Component = ExtendOngoingParkingConfirm;
      headline = t('parking.extendParkingConfirm');
      break;
    case 'showOngoingMultipleSessions':
      Component = OngoingMultipleSessionsModalContent;
      headline = t('parking.ongoingActiveServices');
      break;
    case 'parkingPaymentMethod':
      Component = SelectPaymentMethodModalContent;
      headline = t('parking.parkingProcess.selectPaymentMethod');
      break;
    case 'subscriptionPaymentMethod':
      Component = SubscriptionPaymentMethodModalContent;
      headline = t('parking.parkingProcess.selectPaymentMethod');
      break;
    case 'anprPaymentInfo':
      Component = AnprPaymentMethodModalContent;
      headline = t('parking.anprPaymentInfoTitle');
      break;
    case 'unsavedEditVehicle':
      Component = UnsavedEditVehicleConfirm;
      headline = t('vehicle.unsavedChanges.title');
      break;
    case 'unsavedEditPermit':
      Component = UnsavedEditPermitConfirm;
      headline = t('vehicle.unsavedChanges.title');
      break;
    case 'deleteVehicle':
      Component = ConfirmVehicleRemoval;
      headline = t('vehicle.removeVehicle');
      break;
    case 'paymentCard':
      Component = PaymentCardContent;
      headline = paymentCard.title;
      subtitle = paymentCard.subtitle;
      break;
    case 'confirmPaymentRemoval':
      Component = ConfirmPaymentMethodRemoval;
      headline = paymentCard.title;
      subtitle = paymentCard.subtitle;
      break;
    case 'productUnavailable':
      Component = UnavailableModalContent;
      headline = t('longTermParking.unavailableHeadline');
      break;
    case 'parkingHistoryFilter':
      Component = ParkingHistoryFilterModalContent;
      headline = t('parkingHistoryFilter.headline');
      allowScrollableContent = false;
      break;
    case 'stopCharging':
      Component = StopCharging;
      headline = t('evCharging.stop');
      break;
    case 'confirmPermit':
      Component = ConfirmPurchasePermitModalContent;
      headline = freeSalesOrder
        ? t('button.redeem')
        : t('services.confirmPermit');
      break;
    case 'terminatePermit':
      Component = ConfirmCancelPermitModalContent;
      headline =
        confirmDisableRenewalOnly || showDisableRenewalDialog
          ? t('permit.disableAutomaticRenewal')
          : t('services.cancelPermit');
      break;
    case 'duplicatePermitWarning':
      Component = () => (
        <ItemUnavailableModalContent contentType="duplicatePermitWarning" />
      );
      headline = t('longTermParking.duplicateHeadline');
      break;
    case 'permitActivationNeeded':
      Component = () => (
        <ItemUnavailableModalContent contentType="permitActivationNeeded" />
      );
      headline = t('longTermParking.permitActivationNeeded');
      break;
    case 'limitReach':
      Component = LimitReachModalContent;
      headline = t('longTermParking.limitReach');
      break;
    case 'privacyPolicy':
    case 'termsAndConditions':
      Component = () => <TermsPdfModalContent document={modalType} />;
      headline = t(
        modalType === 'privacyPolicy'
          ? 'account.terms.privacyPolicy'
          : 'account.terms.termsAndConditions'
      );
      break;
    case 'confirmPrepaidPurchase':
      Component = PrepaidTicketConfirmModalContent;
      headline = t('parking.streetParkingConfirmTitle');
      break;
    case 'hslTicketRenderView':
      Component = HslTicketRenderModalContent;
      headline = titleTypeText(hslTicket?.ticketType);
      break;
    case 'setParkingReminder':
      Component = SetParkingReminderModalContent;
      headline = t('account.setReminder.title');
      break;
    case 'unlockTariff':
      Component = UnlockTariffModalContent;
      headline = t('parking.tariff.unlockTariff');
      break;
    case 'appLocation':
      Component = AppLocation;
      headline = t('account.appLocation');
      break;
    case 'parkingAppLocation':
      Component = () => (
        <UpdateFeatureCountryModal isAutomatic={activeZoneId === ''} />
      );
      headline = t('parking.updateLocation.title');
      break;
    case 'removeAccount':
      Component = RemoveAccount;
      headline = t('account.removeConfirm');
      break;
    case 'accountRemovalFailed':
      Component = AccountRemovalFailed;
      headline = t('account.removeFailed');
      break;
    case 'preventAccountRemoval':
      Component = AccountRemovalPrevented;
      headline = canContinueRemoval
        ? t('account.removeAccount')
        : t('account.remove.prevented');
      break;
    case 'accountRemovalSuccess':
      Component = AccountRemovalSuccess;
      headline = t('account.removeSuccess');
      break;
    case 'accountRemovalErrors':
      Component = AccountRemovalErrors;
      headline = t('account.removeFailed');
      break;
    case 'accountRemovalErrorLinkedService':
      Component = AccountRemovalPreventedLinkedService;
      headline = t('account.removeFailed');
      break;
    case 'confirmStartParking':
      Component = ConfirmParkingClosedZoneModalContent;
      headline = t('parking.closedZoneConfirm');
      break;
    case 'accountRestricted':
      Component = AccountRestrictedModalContent;
      break;
    case 'selectCountryForSupport':
      Component = ContactUsSelectCountry;
      headline = t('account.contactUs.support');
      break;
    case 'language':
      Component = LanguageList;
      headline = t('language.title');
      break;
    case 'noPaymentRequired':
      Component = NoPaymentRequiredModalContent;
      headline = t('permit.access.paymentNotRequired');
      break;
    case 'defaultPaymentMethod':
      Component = DefaultPaymentMethodModalContent;
      headline = t('paymentMethods.defaultMethod');
      break;
    case 'firstTimer':
      Component = FirstTimer;
      headline = undefined;
      break;
    case 'remoteTutorial':
      Component = RemoteContentTutorial;
      headline = undefined;
      break;
    case 'swishInfo':
      Component = SwishInfo;
      headline = 'Swish';
      break;
    case 'registrationPlates':
    case 'parkingSpaces':
      Component = () => (
        <PermitOrderLineModalContent propertyName={modalType} />
      );
      headline = t(`permit.${modalType}`);
      break;
    case 'parkingSpacesByPermitId':
      Component = PermitParkingSpaceModalContent;
      headline = t('permit.parkingSpaces');
      break;
    case 'zoneFilter':
      Component = ZoneFilterModal;
      headline = t('search.zoneFilter.title');
      break;
    case 'billingIntervals':
      Component = BillingIntervalsModalContent;
      headline = t('longTermParking.billingFrequency');
      break;
    case 'cancelCharging':
      Component = CancelCharging;
      headline = t('button.cancel');
      break;
    case 'NewParkingSessionPriceBreakdownDetails':
      Component = NewParkingSessionPriceBreakdownDetails;
      headline = t('price.priceDetails');
      break;
    case 'OngoingParkingSessionPriceBreakdownDetails':
      Component = OngoingParkingSessionPriceBreakdownDetails;
      headline = t('price.priceDetails');
      break;
    case 'unlockPmc':
      Component = UnlockPmcModalContent;
      headline = t('permit.unlockPMC.title');
      break;
    case 'unlockMemberGroupPmc':
      Component = UnlockMemberGroupPmcModalContent;
      headline = t('permit.unlockPMC.title');
      break;
    case 'unlockMemberGroup':
      Component = UnlockMemberGroupModalContent;
      headline = unlockMemberGroupSuccessVar
        ? t('longTermParking.staffMemberPermit.codeVerified')
        : t('longTermParking.staffMemberPermit.unlock.title');
      break;
    case 'OpenBarrierManually':
      Component = OpenBarrierManually;
      headline = t('parking.OpenBarrierManually');
      break;
    case 'rfidRegister':
      Component = RegisterRfidModalContent;
      headline = t('account.tagsAndCards.title');
      break;
    case 'editOrDeleteRfid':
      Component = EditOrDeleteRfid;
      headline = tagName;
      break;
    case 'confirmRfidTagRemoval':
      Component = ConfirmRfidTagRemoval;
      headline = tagName;
      break;
    case 'rfidScan':
      Component = RfidTagScanModal;
      headline = tagName || t('account.tagsAndCards.addNewTag');
      break;
    case 'rfidScanComplete':
      Component = RfidScanCompleteModal;
      headline = tagName || t('account.tagsAndCards.addNewTag');
      break;
    case 'leaveWaitingListModal':
      Component = LeaveWaitingListModal;
      headline = t('button.reject');
      break;
    case 'PermitHsaModal':
      Component = PermitHsaModalContent;
      headline =
        hsaIdState.customerVerificationStatus === 'success'
          ? t('longTermParking.staffMemberPermit.codeVerified')
          : t('longTermParking.staffMemberPermit.hsa.title');
      break;
    case 'addOtherPaymentMethod':
      Component = AddOtherPaymentMethod;
      headline = t('paymentMethods.addPaymentMethod');
      break;
    case 'mobilePayConfirmation':
      Component = MobilePayConfirmation;
      headline = t('paymentMethods.mobilePay');
      break;
    case 'walletTokenEdit':
      Component = WalletTokenEdit;
      headline = t('paymentMethods.mobilePay');
      break;
    case 'cancelSubscription':
      Component = CancelSubscriptionModal;
      headline = t('account.aimoSubscription.modalTitle');
      break;
    case 'connectBenefit':
      Component = EmailVerificationModal;
      headline = t('account.connectBenefit');
      break;
    case 'benefitDetail':
      Component = BenefitDetailModal;
      headline = t('account.benefitDetail.title');
      break;
    case 'disconnectBenefit':
      Component = DisconnectBenefitModal;
      headline = t('account.benefitDetail.disconnectTitle');
      break;
    default:
      break;
  }

  return (
    <RNWebModal
      showBackDrop={true}
      backDropOpacity={backDropOpacity}
      headline={headline}
      subtitle={subtitle}
      allowScrollableContent={allowScrollableContent}
      forceOpen={forceOpen}
      hideCloseButton={hideCloseButton}
      buttonType={getModalButtonType(modalType)}>
      <Component />
    </RNWebModal>
  );
};

export default Modal;
